import { usePageModels } from '@/context/PageModels';
import { withGTM } from '@/features/shared/HOCs';
import { resolveLink } from '@/utils/resolvers';
import { BrManageContentButton } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import React, { FunctionComponent, memo, useMemo, useRef } from 'react';
import GlobalFooter from './GlobalFooter';
import { Props as GlobalFooterProps } from './GlobalFooter/GlobalFooter.interface';
import {
  onDatalayerClickCTA,
  onDatalayerClickLink,
  onDatalayerLegacy,
  onDatalayerLiveChat,
} from './GlobalFooterContainer.datalayer';
import useStyles from './GlobalFooterContainer.style';

const GlobalFooterContainer: FunctionComponent<Br.Props> = ({ component, page }: Br.Props) => {
  const pageModels = usePageModels();
  const classes = useStyles();
  const {
    document: documentRef,
    'globalFooter.config': globalFooterConfig,
    'globalFooter.labels': globalFooterLabels,
    componentParameterMap,
  } = component.getModels();
  const listDocument: Br.Content = useMemo(
    () => documentRef && page?.getContent(documentRef),
    [documentRef, page],
  );
  const isPreview = useMemo(() => page?.isPreview(), [page]);

  const { contentItem: menuListRef } = useMemo(
    () => listDocument?.getData<any>() || {},
    [listDocument],
  );

  const sectionDocuments = useRef<any[]>([]);

  const globalFooterProps: GlobalFooterProps | undefined = useMemo(() => {
    sectionDocuments.current = [];

    return {
      datalayerVariant: componentParameterMap?.['variant-label'] || '',
      ...(menuListRef && {
        categories: menuListRef.map((itemRef: any) => {
          const sectionDocument: Br.Content = page?.getContent(itemRef);
          const { name, footerNavigationColumnTitle, menuGroup } =
            sectionDocument?.getData<any>() || {};

          sectionDocuments.current.push({ name, document: sectionDocument });

          return {
            name,
            title:
              footerNavigationColumnTitle &&
              resolveLink({
                rawVal: footerNavigationColumnTitle,
                page,
                pageModels,
              }),
            links: menuGroup
              .map((group: any) => {
                const { contentList } = group;
                return contentList
                  .map((content: any) => {
                    const { menuItem } = content;

                    return (
                      menuItem.length
                        ? menuItem.map((item: any) => {
                            const { link, includeInFooter } = item;
                            return {
                              link:
                                link[0] &&
                                resolveLink({
                                  rawVal: link,
                                  page,
                                  pageModels,
                                }),
                              includeInFooter,
                            };
                          })
                        : [
                            {
                              link:
                                menuItem.link &&
                                menuItem.link[0] &&
                                resolveLink({
                                  rawVal: menuItem.link,
                                  page,
                                  pageModels,
                                }),
                              includeInFooter: menuItem.includeInFooter,
                            },
                          ]
                    )
                      .filter((item: any) => item.includeInFooter)
                      .map((item: any) => item.link);
                  })
                  .reduce((a: any, b: any) => [...a, ...b], []);
              })
              .reduce((a: any, b: any) => [...a, ...b], []),
          };
        }),
      }),
    };
  }, [componentParameterMap, menuListRef, page, pageModels]);

  const listButtonClasses = useMemo(
    () => clsx(classes.listButtonClass, isPreview && 'is-preview'),
    [classes.listButtonClass, isPreview],
  );
  const sectionButtonClasses = useMemo(
    () => clsx(classes.sectionButtonClass, isPreview && 'is-preview'),
    [classes.sectionButtonClass, isPreview],
  );

  const renderContentButton = (slotName: string, document: any, buttonClasses: string) => (
    <div key={slotName} className={buttonClasses} slot={slotName}>
      {document && <BrManageContentButton content={document} />}
    </div>
  );

  return (
    <GlobalFooter
      config={globalFooterConfig}
      labels={globalFooterLabels}
      categories={globalFooterProps?.categories}
      datalayerVariant={globalFooterProps?.datalayerVariant}
    >
      {renderContentButton('menuList', listDocument, listButtonClasses)}
      {sectionDocuments?.current.map((section: any) =>
        renderContentButton(section.name, section.document, sectionButtonClasses),
      )}
    </GlobalFooter>
  );
};

const gtmConfig = { eventName: 'gtm:click:link', isCustom: true };
const GlobalFooterContainerLegacy = withGTM({
  Component: GlobalFooterContainer,
  onDatalayer: onDatalayerLegacy,
  config: gtmConfig,
});
const GlobalFooterContainerLiveChat = withGTM({
  Component: GlobalFooterContainerLegacy,
  onDatalayer: onDatalayerLiveChat,
  config: gtmConfig,
});
const GlobalFooterContainerGlobal = withGTM({
  Component: GlobalFooterContainerLiveChat,
  onDatalayer: onDatalayerClickLink,
  config: gtmConfig,
});
const GlobalFooterContainerGTM = withGTM({
  Component: GlobalFooterContainerGlobal,
  onDatalayer: onDatalayerClickCTA,
  config: { eventName: 'gtm:click:cta', isCustom: true },
});

export default memo(GlobalFooterContainerGTM);
